import { useEffect, useRef } from "react";

interface DestinationSearchInputProps {
  value: string;
  onChange: (newValue: string) => void;
  onCoordinates?: (lat: number, lng: number) => void;
}

function formatPlace(place: google.maps.places.PlaceResult): string {
  // Nur den Ortsnamen verwenden (z.B. "Crowne Plaza Dublin Airport by IHG")
  // Keine langen Adress-Strings – sauber für die Timeline-Anzeige
  return place.name || "";
}

export default function DestinationSearchInput({
  value,
  onChange,
  onCoordinates,
}: DestinationSearchInputProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const legacyInputRef = useRef<HTMLInputElement | null>(null);

  const onChangeRef = useRef(onChange);
  const onCoordsRef = useRef(onCoordinates);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onCoordsRef.current = onCoordinates; }, [onCoordinates]);

  // Sync: externen Value-Prop ins Input-Element schreiben (für Edit-Modus)
  // Programmatisches .value-Setzen löst kein input-Event aus → kein overwriting
  useEffect(() => {
    if (legacyInputRef.current && legacyInputRef.current.value !== value) {
      legacyInputRef.current.value = value;
    }
  }, [value]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !window.google?.maps?.places) {
      console.error("❌ Google Maps Places API nicht geladen.");
      return;
    }

    const input = document.createElement("input");
    input.type = "text";
    input.value = value; // Vorausfüllen für Edit-Modus
    input.placeholder = "Ort suchen...";
    input.className =
      "w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400 placeholder:text-slate-600";
    legacyInputRef.current = input;
    container.appendChild(input);

    const ac = new google.maps.places.Autocomplete(input, {
      fields: ["name", "geometry", "address_components"],
    });

    ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (!place?.name) return;

      const formatted = formatPlace(place);
      // Programmatisches Setzen löst KEIN input-Event aus → kein Race-Condition
      input.value = formatted;
      onChangeRef.current(formatted);

      if (place.geometry?.location && onCoordsRef.current) {
        onCoordsRef.current(
          place.geometry.location.lat(),
          place.geometry.location.lng()
        );
      }
    });

    // Manuelle Eingabe ohne Auswahl (z.B. Feld leeren)
    const handleInput = () => onChangeRef.current(input.value);
    input.addEventListener("input", handleInput);

    return () => {
      input.removeEventListener("input", handleInput);
      if (container.contains(input)) container.removeChild(input);
      legacyInputRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <div ref={containerRef} className="w-full mb-2" />;
}
